/**
 * Quick Calculator Context
 *
 * Provides shared state for the Quick Calculator across all components.
 * This ensures the trigger button, popover, and keyboard shortcut all
 * operate on the same state instance.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useQuickCalculatorState, type UseQuickCalculatorReturn } from './useQuickCalculator';

const QuickCalculatorContext = createContext<UseQuickCalculatorReturn | null>(null);

interface QuickCalculatorProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps the app and provides calculator state
 */
export function QuickCalculatorProvider({ children }: QuickCalculatorProviderProps) {
  const calculator = useQuickCalculatorState();

  return (
    <QuickCalculatorContext.Provider value={calculator}>{children}</QuickCalculatorContext.Provider>
  );
}

/**
 * Hook to access the shared calculator state
 * Must be used within a QuickCalculatorProvider
 */
export function useQuickCalculator(): UseQuickCalculatorReturn {
  const context = useContext(QuickCalculatorContext);

  if (!context) {
    throw new Error('useQuickCalculator must be used within a QuickCalculatorProvider');
  }

  return context;
}
